<template>
    <Page class="compliance-evidence-page">
        <template #title>
            <div class="header-section">
                <Button
                    icon="pi pi-arrow-left"
                    text
                    severity="secondary"
                    @click="goBack"
                    class="back-button"
                />
                <div class="title-content">
                    <div class="breadcrumb">
                        <span class="breadcrumb-item">Live Mode</span>
                        <i class="pi pi-angle-right"></i>
                        <span class="breadcrumb-item">Process Dashboard</span>
                        <i class="pi pi-angle-right"></i>
                        <span class="breadcrumb-item-current"
                            >Compliance Evidence</span
                        >
                    </div>
                    <h1 class="page-title">
                        <i class="pi pi-shield"></i>
                        Compliance Evidence
                    </h1>
                    <div v-if="businessKey" class="business-key">
                        {{ businessKey }}
                    </div>
                </div>
            </div>
        </template>

        <Section>
            <template #title>
                <div class="section-header">
                    <span>Evidence Records</span>
                    <Tag
                        v-if="!loading"
                        :value="`${evidence.length} ${evidence.length === 1 ? 'record' : 'records'}`"
                        severity="info"
                    />
                </div>
            </template>

            <DataTable
                :value="evidence"
                :loading="loading"
                stripedRows
                paginator
                :rows="20"
                :rowsPerPageOptions="[10, 20, 50, 100]"
                sortField="timestamp"
                :sortOrder="-1"
                class="evidence-table"
                responsiveLayout="scroll"
                @row-click="viewEvidenceDetail"
            >
                <template #empty>
                    <div class="empty-state">
                        <i class="pi pi-info-circle"></i>
                        <p>
                            No compliance evidence found for this process
                            instance
                        </p>
                    </div>
                </template>

                <Column header="" style="width: 50px">
                    <template #body="slotProps">
                        <i
                            :class="getEventIcon(slotProps.data)"
                            :title="getEventTooltip(slotProps.data)"
                            class="event-icon"
                        ></i>
                    </template>
                </Column>

                <Column field="description" header="What" style="width: 28%">
                    <template #body="slotProps">
                        <div class="description-cell">
                            {{ slotProps.data.description }}
                        </div>
                    </template>
                </Column>

                <Column field="value" header="Evidence" style="width: 35%">
                    <template #body="slotProps">
                        <a
                            v-if="slotProps.data.type === 'uri'"
                            :href="slotProps.data.value"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="evidence-link"
                            @click.stop
                        >
                            {{ truncateUrl(slotProps.data.value, 60) }}
                            <i class="pi pi-external-link" />
                        </a>
                        <div v-else class="evidence-text">
                            {{ slotProps.data.value }}
                        </div>
                    </template>
                </Column>

                <Column
                    field="process_element_name"
                    header="Task/Event"
                    style="width: 20%"
                >
                    <template #body="slotProps">
                        <div class="context-cell">
                            <span
                                v-if="slotProps.data.process_element_name"
                                class="task-name"
                            >
                                {{ slotProps.data.process_element_name }}
                            </span>
                            <span
                                v-else-if="slotProps.data.process_element_id"
                                class="element-id"
                                :title="slotProps.data.process_element_id"
                            >
                                Task (unknown name)
                            </span>
                            <span v-else class="event-type">
                                {{ slotProps.data.event_type }}
                            </span>
                        </div>
                    </template>
                </Column>

                <Column
                    field="timestamp"
                    header="When"
                    sortable
                    style="width: 15%"
                >
                    <template #body="slotProps">
                        <div class="timestamp-cell">
                            {{ formatDateTime(slotProps.data.timestamp) }}
                        </div>
                    </template>
                </Column>
            </DataTable>
        </Section>
    </Page>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Tag from "primevue/tag";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import { useStekStore } from "@/composables/stek-manager";
import { type types, useClient } from "@/lib/openapi";
import { startStekLiveMgtGetComplianceEvidenceStartPost } from "@/lib/openapi/gen/sdk.gen";

type ComplianceEvidenceView = types.ComplianceEvidenceView;

defineOptions({
    name: "ComplianceEvidencePage",
});

const route = useRoute();
const router = useRouter();

const stekStore = useStekStore();
const { stek } = storeToRefs(stekStore);

const client = useClient({
    requires: {
        auth: true,
        organization: true,
        stek: true,
    },
});

const evidence = ref<ComplianceEvidenceView[]>([]);
const loading = ref(false);
const businessKey = ref<string | null>(null);

const processInstanceId = route.query.process_instance_id as string;
businessKey.value = (route.query.business_key as string) || null;

const fetchEvidence = async () => {
    if (!processInstanceId) {
        return;
    }

    loading.value = true;
    try {
        const { data } =
            await startStekLiveMgtGetComplianceEvidenceStartPost<true>({
                client,
                throwOnError: true,
                body: {
                    process_instance_id: processInstanceId,
                    hours: 720, // 30 days
                },
            });
        evidence.value = data;
    } catch (error) {
        console.error("Failed to fetch compliance evidence:", error);
        evidence.value = [];
    } finally {
        loading.value = false;
    }
};

const formatDateTime = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
};

const getEventIcon = (evidence: ComplianceEvidenceView): string => {
    const eventType = evidence.event_type?.toLowerCase() || "";

    // Task completed
    if (
        eventType.includes("task_completed") ||
        eventType.includes("task_finished")
    ) {
        return "pi pi-check-circle";
    }

    // Process completed
    if (
        eventType.includes("process_completed") ||
        eventType.includes("process_finished")
    ) {
        return "pi pi-flag-fill";
    }

    // No icon for other events
    return "";
};

const getEventTooltip = (evidence: ComplianceEvidenceView): string => {
    const eventType = evidence.event_type?.toLowerCase() || "";

    if (eventType.includes("task_completed")) {
        return "Task completed";
    }
    if (eventType.includes("process_completed")) {
        return "Process completed";
    }

    return "";
};

const goBack = () => {
    router.back();
};

const viewEvidenceDetail = (event: { data: ComplianceEvidenceView }) => {
    // Store evidence in sessionStorage for quick access
    sessionStorage.setItem(
        `evidence_${event.data.id}`,
        JSON.stringify(event.data)
    );

    router.push({
        name: "complianceEvidenceDetailPage",
        params: {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
        },
        query: {
            process_instance_id: processInstanceId,
            business_key: businessKey.value || undefined,
            evidence_id: event.data.id,
        },
    });
};

// Wait for stek to be available before fetching
watch(
    () => stek.value,
    (newStek) => {
        if (newStek && processInstanceId) {
            fetchEvidence();
        }
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.compliance-evidence-page {
    max-width: 1600px;
    margin: 0 auto;
}

.header-section {
    display: flex;
    align-items: flex-start;
    gap: 1rem;

    .back-button {
        margin-top: 0.5rem;
    }

    .title-content {
        flex: 1;

        .breadcrumb {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.875rem;
            color: var(--text-color-secondary);
            margin-bottom: 0.5rem;

            .breadcrumb-item {
                color: var(--text-color-secondary);
            }

            .breadcrumb-item-current {
                color: var(--text-color);
                font-weight: 500;
            }

            i {
                font-size: 0.75rem;
            }
        }

        .page-title {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin: 0 0 0.5rem 0;
            flex-wrap: nowrap;
            white-space: nowrap;

            i {
                color: var(--primary-color);
                font-size: 1.75rem;
                flex-shrink: 0;
            }
        }

        .business-key {
            font-size: 0.875rem;
            color: var(--text-color-secondary);
            font-family: monospace;
            background: var(--surface-100);
            padding: 0.25rem 0.5rem;
            border-radius: var(--border-radius);
            display: inline-block;
        }
    }
}

.section-header {
    display: inline-flex !important;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: nowrap;
    white-space: nowrap;
}

.evidence-table {
    :deep(.p-datatable-wrapper) {
        overflow-x: auto;
    }

    :deep(.p-datatable-thead > tr > th) {
        white-space: nowrap;
    }

    :deep(.p-datatable-tbody > tr) {
        cursor: pointer;

        &:hover {
            background-color: var(--surface-hover);
        }
    }
}

.event-icon {
    font-size: 1.25rem;

    &.pi-check-circle {
        color: var(--green-500);
    }

    &.pi-flag-fill {
        color: var(--blue-500);
    }
}

.empty-state {
    text-align: center;
    padding: 3rem;
    color: var(--text-color-secondary);

    i {
        font-size: 3rem;
        margin-bottom: 1rem;
        opacity: 0.5;
    }

    p {
        margin: 0;
        font-size: 1rem;
    }
}

.description-cell {
    line-height: 1.5;
    padding: 0.5rem 0;
    word-wrap: break-word;
    white-space: normal;
    font-size: 0.875rem;
}

.evidence-link {
    color: var(--primary-color);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    word-break: break-word;
    font-size: 0.875rem;

    i {
        font-size: 0.813rem;
        flex-shrink: 0;
    }

    &:hover {
        text-decoration: underline;
    }
}

.evidence-text {
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.5;
    font-size: 0.875rem;
}

.context-cell {
    font-size: 0.875rem;

    .task-name {
        color: var(--text-color);
        font-weight: 500;
    }

    .element-id {
        color: var(--text-color-secondary);
        font-style: italic;
        cursor: help;
    }

    .event-type {
        color: var(--text-color-secondary);
        font-style: italic;
    }
}

.timestamp-cell {
    white-space: nowrap;
    font-size: 0.813rem;
    color: var(--text-color-secondary);
}

.text-muted {
    color: var(--text-color-secondary);
}
</style>
